import { InputAdornment, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import DeleteIcon from "@material-ui/icons/Delete";
import Checkbox from "@saleor/components/Checkbox";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import TableHead from "@saleor/components/TableHead";
import { VoucherVariantDetails_voucher_productVariants } from "@saleor/discounts/types/VoucherVariantDetails";
// import TableCellAvatar from "@saleor/components/TableCellAvatar";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
// import { maybe } from "@saleor/misc";
import {
  SearchProducts_search_edges_node,
  SearchProducts_search_edges_node_variants
} from "@saleor/searches/types/SearchProducts";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import { VoucherCatalogueInput } from "@saleor/types/globalTypes";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";

export interface FormData {
  products: SearchProducts_search_edges_node[];
  query: string;
  onBrandChange(event: React.ChangeEvent<any>);
}

const useStyles = makeStyles(
  theme => ({
    allCheckbox: {
      width: 100
    },
    allFields: {
      marginLeft: "10px",
      marginRight: "10px"
    },
    allSpan: {
      textAlign: "center"
    },
    allSubsidy: {
      width: "80%"
    },
    allSusidyMain: {
      display: "flex",
      justifyContent: "center"
    },
    allText: {
      fontSize: "1rem",
      marginTop: "10px",
      width: "20%"
    },
    avatar: {
      "&&:first-child": {
        paddingLeft: 0
      },
      width: 72
    },
    buttonAction: {
      display: "flex",
      justifyContent: "center"
    },
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    colActions: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 76 + theme.spacing(0.5)
    },
    colName: {
      paddingLeft: 0
    },
    colPrice: {
      textAlign: "center",
      width: "20%"
    },
    content: {
      overflowY: "hidden"
    },
    dialog: {
      height: "calc(100% - 64px)",
      maxHeight: 700
    },
    loadMoreLoaderContainer: {
      alignItems: "center",
      display: "flex",
      height: theme.spacing(3),
      justifyContent: "center",
      marginTop: theme.spacing(3)
    },
    scrollArea: {
      overflowY: "scroll"
    },
    searchBox: {
      display: "flex",
      marginBottom: "10px",
      paddingLeft: "10px",
      paddingRight: "20px"
    },
    selectAll: {
      display: "flex"
    },
    selectallcount: {
      color: theme.palette.primary.main
    },
    tabs: {
      alignItems: "center",
      display: "flex",
      marginBottom: "10px"
    }
  }),
  { name: "AssignDiscountProductDialog" }
);

const useScrollableStyles = makeStyles(
  theme => ({
    content: {
      overflowY: "hidden"
    },
    dialog: {
      height: "calc(100% - 64px)",
      maxHeight: 700,
      maxWidth: "calc(100% - 64px)"
    },
    loadMoreLoaderContainer: {
      alignItems: "center",
      display: "flex",
      height: theme.spacing(3),
      justifyContent: "center",
      marginTop: theme.spacing(3)
    },
    scrollArea: {
      overflowY: "scroll"
    }
  }),
  { name: "AssignDiscountProductDialog" }
);

export interface ProductSubsidyItem {
  id: string;
  name: string;
  retailPrice: string;
  tenantSubsidy: string;
  smoSubsidy: string;
}

export interface ProductVariantSelection {
  id: string;
  productName: string;
  name: string;
  price: number | null;
  tenantSubsidy: string;
  smoSubsidy: string;
}

export interface AssignDiscountProductDialogProps extends FetchMoreProps {
  brands: any;
  confirmButtonState: ConfirmButtonTransitionState;
  open: boolean;
  products: SearchProducts_search_edges_node[];
  loading: boolean;
  onClose: () => void;
  onFetch: (value: string) => void;
  onSubmit: (data: VoucherCatalogueInput) => void;
  onSelectBrand: (value: string) => void;
  fetchBrands?: (query: string) => void;
  fetchMoreBrands?: FetchMoreProps;
  variants: VoucherVariantDetails_voucher_productVariants;
  onFetchMoreVariants: () => void;
}

function handleProductAssign(
  variant: SearchProducts_search_edges_node_variants,
  product: SearchProducts_search_edges_node,
  isSelected: boolean,
  selectedProducts: ProductVariantSelection[],
  setSelectedProducts: (data: ProductVariantSelection[]) => void
) {
  if (isSelected) {
    setSelectedProducts(
      selectedProducts.filter(
        selectedProduct => selectedProduct.id !== variant.id
      )
    );
  } else {
    setSelectedProducts([
      ...selectedProducts,
      {
        id: variant.id,
        name: variant.name,
        price: variant.price.amount,
        productName: product.name,
        smoSubsidy: "100",
        tenantSubsidy: "0"
      }
    ]);
  }
}

function handleSelectAll(
  products: SearchProducts_search_edges_node[],
  setSelectedProducts: (data: ProductVariantSelection[]) => void
) {
  const getAll = [];
  products.forEach(product => {
    product.variants.forEach(variant => {
      if (
        !getAll.some(selectedVariants => selectedVariants.id === variant.id)
      ) {
        getAll.push({
          id: variant.id,
          name: variant.sku,
          price: variant.price.amount,
          productName: product.name,
          sellingPrice: "0",
          smoSubsidy: "100",
          tenantSubsidy: "0"
        });
      }
    });
  });
  setSelectedProducts(getAll);
}

function handlePreloadVariants(
  variants: VoucherVariantDetails_voucher_productVariants,
  selectedProducts: ProductVariantSelection[],
  setSelectedProducts: (data: ProductVariantSelection[]) => void,
  onLoadMore: () => void
) {
  const preloadSelection = variants.edges.map(variant => ({
    id: variant.node.id,
    name: variant.node.sku,
    price: variant.node.price.amount,
    productName: variant.node.product.name,
    smoSubsidy: variant.node.smoShare,
    tenantSubsidy: variant.node.tenantShare
  }));
  setSelectedProducts([...selectedProducts, ...preloadSelection]);

  if (variants.pageInfo.hasNextPage) {
    onLoadMore();
  }
}

const AssignProductSubsidyDialog: React.FC<AssignDiscountProductDialogProps> = props => {
  const {
    brands,
    confirmButtonState,
    hasMore,
    open,
    loading,
    products,
    onClose,
    onFetch,
    onFetchMore,
    onSubmit,
    onSelectBrand,
    fetchBrands,
    fetchMoreBrands,
    variants,
    onFetchMoreVariants
  } = props;
  const classes = useStyles(props);
  const scrollableClassess = useScrollableStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});

  const intl = useIntl();
  const [query, onQueryChange] = useSearchQuery(onFetch);
  const [selectedProducts, setSelectedProducts] = React.useState<
    ProductVariantSelection[]
  >([]);
  const [shareErrors, setShareErrors] = React.useState<ProductSubsidyItem[]>(
    []
  );
  const [subsidyData, setSubsidyData] = React.useState<ProductSubsidyItem[]>(
    []
  );
  const container = React.useRef<HTMLDivElement>();
  const [step, setStep] = React.useState("SelectProducts");
  const [isSelectAll, setSelectAll] = React.useState(false);
  const [isLoadingAll, setIsLoadingAll] = React.useState(false);
  const [forAllSubsidy, setForAllSubsidy] = React.useState(false);
  const [allTenantValue, setAllTenantValue] = React.useState("0");
  const [allSMOValue, setAllSMOValue] = React.useState("100");
  const [internalSearchValue, setInternalSearchValue] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setSelectedProducts([]);
      setSubsidyData([]);
      setShareErrors([]);
      setStep("SelectProducts");
    }
  }, [open]);

  React.useEffect(() => {
    if (variants && open) {
      handlePreloadVariants(
        variants,
        selectedProducts,
        setSelectedProducts,
        onFetchMoreVariants
      );
    }
  }, [variants, open]);

  React.useEffect(() => {
    if (step === "DiscountSubsidy" && isSelectAll) {
      setIsLoadingAll(true);
      if (hasMore) {
        onFetchMore();
      } else {
        handleSelectAll(products, setSelectedProducts);
        setIsLoadingAll(false);
      }
    } else if (step === "SelectProducts" && isSelectAll) {
      handleSelectAll(products, setSelectedProducts);
    }
  }, [step, hasMore, products]);

  React.useEffect(() => {
    const newArray: ProductSubsidyItem[] = [];
    selectedProducts.forEach(product => {
      const findSubsidy = subsidyData.filter(
        subsidy => subsidy.id === product.id
      );
      if (findSubsidy.length === 0) {
        const thisProduct: ProductSubsidyItem = {
          id: product.id,
          name: `${product.productName} (${product.name})`,
          retailPrice: `${product.price}`,
          smoSubsidy: product.smoSubsidy,
          tenantSubsidy: product.tenantSubsidy
        };

        newArray.push(thisProduct);
      }
    });
    setSubsidyData([
      ...newArray,
      ...subsidyData.filter(({ id }) =>
        selectedProducts.map(({ id }) => id).includes(id)
      )
    ]);
  }, [selectedProducts]);

  // const handleSubmit = () => onSubmit(selectedProducts);
  const handleSubmit = () => {
    setShareErrors([]);
    const errors = [];

    if (forAllSubsidy) {
      const allReturn = subsidyData.map(item => ({
        product_variant: item.id,
        smoShare: allSMOValue,
        tenantShare: allTenantValue
      }));
      onSubmit({ productVariantsWithSubsidy: allReturn });

      return;
    }

    subsidyData.forEach(item => {
      if (
        parseFloat(item.smoSubsidy) + parseFloat(item.tenantSubsidy) !==
        100
      ) {
        errors.push(item);
      }
    });
    if (errors.length > 0) {
      setShareErrors(errors);
      return;
    }
    const newReturn = subsidyData.map(item => ({
      product_variant: item.id,
      smoShare: item.smoSubsidy,
      tenantShare: item.tenantSubsidy
    }));
    onSubmit({ productVariantsWithSubsidy: newReturn });
  };

  const containerHeight = container.current?.scrollHeight - 250;
  const [sellerDisplayName, setSellerDisplayName] = useStateFromProps("");
  const initialForm = {
    id: null,
    name: "",
    sellerBrand: ""
  };

  const sellerBrandChoices = maybe(
    () =>
      brands.map(brand => ({
        label: brand?.name + " - " + brand.legalName,
        value: brand?.id
      })),
    []
  );

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submit }) => {
        const handleSelectBrand = createSingleAutocompleteSelectHandler(
          change,
          setSellerDisplayName,
          sellerBrandChoices
        );

        const handleTenantSubsidyChange = e => {
          if (!/^[.0-9\b]+$/.test(e.target.value)) {
            return;
          }

          if (e.target.name === "allTenant") {
            setAllTenantValue(e.target.value);
            return;
          }

          const newArray: ProductSubsidyItem[] = [];
          subsidyData.forEach(product => {
            if (e.target.name === product.id) {
              const thisProduct: ProductSubsidyItem = {
                id: product.id,
                name: product.name,
                retailPrice: product.retailPrice,
                smoSubsidy: product.smoSubsidy,
                tenantSubsidy: e.target.value
              };
              newArray.push(thisProduct);
            } else {
              const thisProduct: ProductSubsidyItem = {
                id: product.id,
                name: product.name,
                retailPrice: product.retailPrice,
                smoSubsidy: product.smoSubsidy,
                tenantSubsidy: product.tenantSubsidy
              };
              newArray.push(thisProduct);
            }
          });
          setSubsidyData(newArray);
        };

        const handleSMOSubsidyChange = e => {
          if (!/^[.0-9\b]+$/.test(e.target.value)) {
            return;
          }

          if (e.target.name === "allSMO") {
            setAllSMOValue(e.target.value);
            return;
          }
          const newArray: ProductSubsidyItem[] = [];
          subsidyData.forEach(product => {
            if (e.target.name === product.id) {
              const thisProduct: ProductSubsidyItem = {
                id: product.id,
                name: product.name,
                retailPrice: product.retailPrice,
                smoSubsidy: e.target.value,
                tenantSubsidy: product.tenantSubsidy
              };
              newArray.push(thisProduct);
            } else {
              const thisProduct: ProductSubsidyItem = {
                id: product.id,
                name: product.name,
                retailPrice: product.retailPrice,
                smoSubsidy: product.smoSubsidy,
                tenantSubsidy: product.tenantSubsidy
              };
              newArray.push(thisProduct);
            }
          });
          setSubsidyData(newArray);
        };

        const handleRemove = id => {
          const filterRemove = subsidyData.filter(item => item.id !== id);
          const filterSelected = selectedProducts.filter(
            item => item.id !== id
          );
          setSelectedProducts(filterSelected);
          setSubsidyData(filterRemove);
          if (filterSelected.length === 0) {
            setStep("SelectProducts");
          }
        };

        let btnDisabled = false;
        if (!formData.sellerBrand) {
          btnDisabled = true;
        } else if (formData.sellerBrand && products && products.length) {
          btnDisabled = false;
        } else if (!formData.sellerBrand && products && products.length) {
          btnDisabled = false;
        } else {
          btnDisabled = true;
        }
        return (
          <Dialog
            onClose={onClose}
            open={open}
            classes={{ paper: scrollableClassess.dialog }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <FormattedMessage
                defaultMessage="Assign Product"
                description="dialog header"
              />
            </DialogTitle>
            <DialogContent
              className={scrollableDialogClasses.content}
              ref={container}
            >
              <div className={classes.tabs}>
                <Button
                  color={step === "SelectProducts" ? "primary" : "default"}
                >
                  <FormattedMessage {...buttonMessages.selectProducts} />
                </Button>
                <span>{`>`}</span>
                <Button
                  color={step === "DiscountSubsidy" ? "primary" : "default"}
                  disabled={step === "SelectProducts"}
                >
                  <FormattedMessage {...buttonMessages.discountSubsidy} />
                </Button>
              </div>
              {step === "SelectProducts" ? (
                <>
                  <SingleAutocompleteSelectField
                    label={intl.formatMessage({
                      defaultMessage: "Brand:"
                    })}
                    name="sellerBrand"
                    displayValue={sellerDisplayName}
                    choices={sellerBrandChoices}
                    disabled={false}
                    value={formData.sellerBrand}
                    onChange={e => {
                      handleSelectBrand(e);
                      onSelectBrand(e.target.value);
                    }}
                    fetchChoices={fetchBrands}
                    {...fetchMoreBrands}
                  />
                  <FormSpacer />
                  <TextField
                    id="searchProductsTxt"
                    disabled={!formData.sellerBrand ? true : false}
                    name="query"
                    value={query}
                    onChange={onQueryChange}
                    label={intl.formatMessage({
                      defaultMessage: "Search Products"
                    })}
                    placeholder={intl.formatMessage({
                      defaultMessage:
                        "Search by product name, attribute, product type etc..."
                    })}
                    fullWidth
                    InputProps={{
                      autoComplete: "off",
                      endAdornment: loading && <CircularProgress size={16} />
                    }}
                  />
                  {formData.sellerBrand ? (
                    <>
                      <FormSpacer />
                      <div
                        className={scrollableDialogClasses.scrollArea}
                        style={{ height: containerHeight }}
                      >
                        <InfiniteScroll
                          pageStart={0}
                          loadMore={onFetchMore}
                          hasMore={hasMore}
                          useWindow={false}
                          loader={
                            <div
                              className={
                                scrollableDialogClasses.loadMoreLoaderContainer
                              }
                              key={0}
                            >
                              <CircularProgress size={16} />
                            </div>
                          }
                          threshold={10}
                        >
                          <ResponsiveTable key="table">
                            <TableBody>
                              <TableRow key="all">
                                <TableCell className={classes.colName}>
                                  Select All
                                </TableCell>
                                <TableCell
                                  className={classes.colPrice}
                                ></TableCell>
                                <TableCell
                                  className={classes.colPrice}
                                ></TableCell>
                                <TableCell
                                  padding="checkbox"
                                  className={classes.checkboxCell}
                                >
                                  <Checkbox
                                    id="selectAllCheckBox"
                                    checked={isSelectAll}
                                    onChange={e => {
                                      setSelectAll(!isSelectAll);
                                      if (e.target.checked) {
                                        handleSelectAll(
                                          products,
                                          setSelectedProducts
                                        );
                                      } else {
                                        setSelectedProducts([]);
                                      }
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                              {products &&
                                products.map((product, index) =>
                                  product.variants.map(variant => {
                                    const isSelected = selectedProducts.some(
                                      selectedProduct =>
                                        selectedProduct.id === variant.id
                                    );

                                    return (
                                      <TableRow
                                        key={variant.id}
                                        id={`productRow${index}`}
                                      >
                                        {/* <TableCellAvatar
                                      className={classes.avatar}
                                      thumbnail={maybe(() => product.thumbnail.url)}
                                    /> */}
                                        <TableCell className={classes.colName}>
                                          {product.name}
                                        </TableCell>
                                        <TableCell className={classes.colPrice}>
                                          {variant.sku}
                                        </TableCell>
                                        <TableCell className={classes.colPrice}>
                                          {variant.name}
                                        </TableCell>
                                        <TableCell
                                          padding="checkbox"
                                          className={classes.checkboxCell}
                                        >
                                          <Checkbox
                                            id={`productCheckboxRow${index}`}
                                            checked={isSelected}
                                            onChange={e => {
                                              if (!e.target.checked) {
                                                setSelectAll(false);
                                              }
                                              handleProductAssign(
                                                variant,
                                                product,
                                                isSelected,
                                                selectedProducts,
                                                setSelectedProducts
                                              );
                                            }}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              {products && products.length
                                ? ""
                                : "No Products Found"}
                            </TableBody>
                          </ResponsiveTable>
                        </InfiniteScroll>
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                </>
              ) : (
                <>
                  <FormSpacer />
                  <div className={classes.searchBox}>
                    <TextField
                      id="internalsearchProductTxt"
                      fullWidth
                      name="internalSearch"
                      label="Search Product"
                      value={internalSearchValue}
                      onChange={e => setInternalSearchValue(e.target.value)}
                      // InputProps={{
                      //   endAdornment: (
                      //     <InputAdornment position="end">
                      //       <Typography
                      //         component="span"
                      //         variant="caption"
                      //       >
                      //         PHP
                      //       </Typography>
                      //     </InputAdornment>
                      //   )
                      // }}
                    />
                  </div>
                  <div className={classes.selectAll}>
                    <div className={classes.allCheckbox}>
                      <Checkbox
                        id="selectAllSubsidyCheckbox"
                        checked={forAllSubsidy}
                        disabled={isLoadingAll}
                        onChange={() => setForAllSubsidy(!forAllSubsidy)}
                      />
                    </div>
                    <div className={classes.allText}>
                      <div>
                        <span>Select All</span>
                      </div>
                      {forAllSubsidy && (
                        <div className={classes.selectallcount}>
                          <span>{`${selectedProducts.length} products selected`}</span>
                        </div>
                      )}
                    </div>
                    <div className={classes.allSubsidy}>
                      <div className={classes.allSusidyMain}>
                        <div className={classes.allFields}>
                          <TextField
                            id="allTenantTxt"
                            disabled={isLoadingAll || !forAllSubsidy}
                            name="allTenant"
                            label="Tenant"
                            value={allTenantValue}
                            onChange={handleTenantSubsidyChange}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography
                                    component="span"
                                    variant="caption"
                                  >
                                    %
                                  </Typography>
                                </InputAdornment>
                              )
                            }}
                          />
                        </div>
                        <div className={classes.allFields}>
                          <TextField
                            id="allSMOTxt"
                            disabled={isLoadingAll || !forAllSubsidy}
                            name="allSMO"
                            label="SMO"
                            value={allSMOValue}
                            onChange={handleSMOSubsidyChange}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography
                                    component="span"
                                    variant="caption"
                                  >
                                    %
                                  </Typography>
                                </InputAdornment>
                              )
                            }}
                          />
                        </div>
                      </div>
                      <div className={classes.allSpan}>
                        <span>
                          {`${"Percentage"} values will apply to all selected products`}{" "}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    className={scrollableDialogClasses.scrollArea}
                    style={{ height: containerHeight }}
                  >
                    {isLoadingAll ? (
                      <div className={classes.allSusidyMain}>
                        <CircularProgress size={16} />
                      </div>
                    ) : (
                      <ResponsiveTable key="table">
                        <TableHead
                          colSpan={5}
                          disabled={false}
                          items={maybe(() => subsidyData.map(edge => edge))}
                          hideCheckBox
                        >
                          <TableCell className={classes.colName}>
                            <span>
                              <FormattedMessage defaultMessage="Product Name" />
                            </span>
                          </TableCell>
                          <TableCell className={classes.colPrice}>
                            <FormattedMessage defaultMessage="Retail Price" />
                          </TableCell>
                          <TableCell className={classes.colPrice}>
                            <FormattedMessage defaultMessage="Tenant Discount" />
                          </TableCell>
                          <TableCell className={classes.colPrice}>
                            <FormattedMessage defaultMessage="SMO Discount" />
                          </TableCell>
                          <TableCell className={classes.colActions} />
                        </TableHead>
                        <TableBody>
                          {subsidyData &&
                            subsidyData
                              .filter(({ name }) =>
                                name.includes(internalSearchValue)
                              )
                              .map((product, index) => {
                                const hasErrors = shareErrors.some(
                                  selectedProduct =>
                                    selectedProduct.id === product.id
                                );

                                return (
                                  <TableRow
                                    key={product.id}
                                    id={`productRow${index}`}
                                  >
                                    {/* <TableCellAvatar
                                    className={classes.avatar}
                                    thumbnail={maybe(() => product.thumbnail.url)}
                                  /> */}
                                    <TableCell className={classes.colName}>
                                      {product.name}
                                    </TableCell>
                                    <TableCell className={classes.colPrice}>
                                      {product.retailPrice}
                                    </TableCell>
                                    <TableCell className={classes.colPrice}>
                                      <TextField
                                        id={`productTenantTxtRow${index}`}
                                        disabled={forAllSubsidy}
                                        name={product.id}
                                        label="Tenant"
                                        fullWidth
                                        error={hasErrors}
                                        helperText={
                                          hasErrors &&
                                          "Tenant and SMOP share should be equal to 100%"
                                        }
                                        value={product.tenantSubsidy}
                                        onChange={handleTenantSubsidyChange}
                                        InputProps={{
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              <Typography
                                                component="span"
                                                variant="caption"
                                              >
                                                %
                                              </Typography>
                                            </InputAdornment>
                                          )
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell className={classes.colPrice}>
                                      <TextField
                                        id={`productSMOTxtRow${index}`}
                                        disabled={forAllSubsidy}
                                        name={product.id}
                                        label="SMO"
                                        fullWidth
                                        error={hasErrors}
                                        value={product.smoSubsidy}
                                        onChange={handleSMOSubsidyChange}
                                        InputProps={{
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              <Typography
                                                component="span"
                                                variant="caption"
                                              >
                                                %
                                              </Typography>
                                            </InputAdornment>
                                          )
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell className={classes.colActions}>
                                      {/* <Button className={classes.buttonAction}> */}
                                      <DeleteIcon
                                        id={`productDeleteBtnRow${index}`}
                                        onClick={() => handleRemove(product.id)}
                                      />
                                      {/* </Button> */}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          {products && products.length
                            ? ""
                            : "No Products Found"}
                        </TableBody>
                      </ResponsiveTable>
                    )}
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              {step === "SelectProducts" ? (
                <Button
                  onClick={onClose}
                  disabled={isLoadingAll}
                  id="cancelBtn"
                >
                  <FormattedMessage {...buttonMessages.back} />
                </Button>
              ) : (
                <Button
                  id="backBtn"
                  onClick={() => setStep("SelectProducts")}
                  disabled={isLoadingAll}
                >
                  <FormattedMessage {...buttonMessages.back} />
                </Button>
              )}
              {step === "SelectProducts" ? (
                <ConfirmButton
                  id="nextBtn"
                  disabled={
                    btnDisabled ||
                    isLoadingAll ||
                    (selectedProducts.length === 0 && isSelectAll === false)
                  }
                  transitionState={confirmButtonState}
                  color="primary"
                  variant="contained"
                  type="submit"
                  onClick={() => setStep("DiscountSubsidy")}
                >
                  <FormattedMessage
                    defaultMessage="Next"
                    description="button"
                  />
                </ConfirmButton>
              ) : (
                <ConfirmButton
                  id="assignProductsBtn"
                  disabled={btnDisabled || isLoadingAll}
                  transitionState={confirmButtonState}
                  color="primary"
                  variant="contained"
                  type="submit"
                  onClick={submit}
                >
                  <FormattedMessage
                    defaultMessage="Assign Products"
                    description="button"
                  />
                </ConfirmButton>
              )}
            </DialogActions>
          </Dialog>
        );
      }}
    </Form>
  );
};
AssignProductSubsidyDialog.displayName = "AssignProductSubsidyDialog";
export default AssignProductSubsidyDialog;
